// B2a (Finding 002) — Financials overview. No placeholder figures: either the real Phase 5 workspace is active (link)
// or the built capabilities are listed as "Available to Activate". Provider-dependent features are Externally Blocked.
import React from "react";
import { Link } from "react-router-dom";
import StatusChip from "../../../components/StatusChip";
import { useAuth } from "../../../lib/auth";
import { FINANCE_ROLES, usePhaseFeature } from "../../../lib/phaseFeatures";

const BUILT = [
  "Contracts, schedule of values and pay applications (create, submit, approve, reject)",
  "Vendor bills (AP) and job costing: budget vs actual vs billed",
  "Lien waivers with legal notice and file gate",
  "Approved change orders flow into the schedule of values and pay applications",
];
const BLOCKED = [
  ["Bank feed (Plaid)", "Cash balances need a bank-feed provider, which is not contracted."],
  ["QuickBooks / Xero sync", "Accounting sync provider not contracted."],
  ["Online payments (Stripe)", "Payments are not enabled. No payment can be taken in the app."],
];

export default function FinancialOverviewTab() {
  const { user } = useAuth();
  const p5 = usePhaseFeature(5, user?.id);
  const active = p5?.enabled && FINANCE_ROLES.includes(user?.role_key);
  return (
    <div data-testid="financial-overview-body" className="space-y-6 min-w-0">
      <div>
        <h2 className="font-display font-semibold text-erf-dark text-lg">Financials</h2>
        <p className="text-xs font-nav text-erf-dark/70 mt-0.5">Only real records are shown. Nothing here is a sample figure.</p>
      </div>
      {p5 === null ? (
        <p role="status" className="text-sm text-erf-dark/60">Checking Financials status…</p>
      ) : active ? (
        <div className="bg-white border border-erf-borderCream rounded-md p-4" data-testid="financial-overview-active">
          <StatusChip status="active" />
          <p className="text-sm font-nav text-erf-dark mt-2">The Financials workspace is active for this workspace.</p>
          <Link to="/phase5/billing" data-testid="financial-overview-open-phase5"
                className="inline-flex min-h-[44px] items-center mt-3 px-4 rounded-sm bg-erf-gold text-erf-dark font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-erf-dark">
            Open Financials workspace
          </Link>
        </div>
      ) : (
        <div className="bg-white border border-erf-borderCream rounded-md p-4" data-testid="financial-overview-available">
          <StatusChip status="available_to_activate" />
          <p className="text-sm font-nav text-erf-dark mt-2">
            The Financials workspace is built but not activated for this workspace. Once it is activated it covers:
          </p>
          <ul className="list-disc pl-5 mt-2 text-sm font-nav text-erf-dark/80 space-y-1">
            {BUILT.map((b) => <li key={b}>{b}</li>)}
          </ul>
        </div>
      )}
      <div className="bg-white border border-erf-borderCream rounded-md p-4" data-testid="financial-overview-blocked">
        <h3 className="font-display font-semibold text-erf-dark">Provider-dependent features</h3>
        <ul className="mt-2 divide-y divide-erf-borderCream">
          {BLOCKED.map(([name, why]) => (
            <li key={name} className="py-2 flex flex-col sm:flex-row sm:items-center gap-2 min-w-0">
              <span className="sm:w-56 font-nav text-sm text-erf-dark font-semibold">{name}</span>
              <StatusChip status="externally_blocked" />
              <span className="text-xs font-nav text-erf-dark/70">{why}</span>
            </li>
          ))}
        </ul>
      </div>
      <p className="text-xs font-nav text-erf-dark/70">The Contracts and Deposits tabs list real CRM records.</p>
    </div>
  );
}
